import { useEffect } from 'react';
import { bezahlterKlickAus, letztenKlickLesen, type LetzterBezahlterKlick } from '@/lib/letzterBezahlterKlick';
import { darfMarketingSpeichern, darfMessungSpeichern } from '@/lib/geraeteSpeicher';
import { refMerken } from '@/lib/empfehlungRef';

// EINWILLIGUNG (Datenstrategie D0, 27.10.2026, § 25 TDDDG): Bis hierher schrieb dieser Hook
// utm_params, first_touch, last_paid_click und click_ids (mit roher gclid/fbclid) bei JEDEM Besuch
// als 90-Tage-Cookie, ohne Einwilligung. Jetzt landet alles zuerst im Arbeitsspeicher der Seite
// (reicht für Anfragen in derselben Sitzung) und erst mit Einwilligung als Cookie:
//   utm_params, first_touch  → Statistik
//   click_ids, last_paid_click (enthält die gclid) → Marketing
// Willigt jemand später im Banner ein, schreibt attributionSpeichern() den Stand dieser Sitzung nach.

const UTM_STORAGE_KEY = 'utm_params';
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;
const UTM_COOKIE_MAX_AGE = 60 * 60 * 24 * 90; // 90 Tage
const FIRST_TOUCH_KEY = 'first_touch';

// Klick-IDs der Ad-Plattformen. Bewusst ein EIGENER Cookie, nicht in utm_params
// mit hineingeschrieben: Google-Autotagging haengt bei aktivem auto-tagging nur
// ?gclid= an, ganz ohne utm_*. Waeren beide im selben Cookie, ginge die gclid in
// genau diesem Fall verloren (utm-Objekt leer → gar nichts gespeichert).
//
// WARUM DAS ZAEHLT: Ohne gclid laesst sich eine Anmeldung nicht als
// Google-Offline-Conversion zurueckmelden — Google kann den Abschluss dann keinem
// Klick zuordnen, und die Kampagne optimiert weiter blind auf Formularaufrufe
// statt auf echte Wartelisten-Eintraege. Vorher las nur das HuTa-Formular die
// gclid, und zwar aus window.location.search BEIM ABSENDEN: wer ueber eine Anzeige
// auf einer anderen Seite landete und sich spaeter hier eintrug, hatte den
// Parameter laengst nicht mehr in der URL. Ergebnis: 0 von 110 Eintraegen mit
// gclid, obwohl nachweislich welche aus Google Ads kamen (utm_medium=cpc).
const CLICK_ID_STORAGE_KEY = 'click_ids';
const CLICK_ID_KEYS = ['gclid', 'fbclid'] as const;

// Letzter bezahlter Klick (15.10.2026): zusätzlich zum First-Touch, wird von JEDEM neuen bezahlten
// Klick überschrieben. Regeln in src/lib/letzterBezahlterKlick.ts.
const LAST_PAID_KEY = 'last_paid_click';

export type UtmParams = Partial<Record<typeof UTM_KEYS[number], string>>;
export type ClickIds = Partial<Record<typeof CLICK_ID_KEYS[number], string>>;

function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : undefined;
}

function writeCookie(name: string, value: string, maxAgeSeconds: number): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${name}=${encodeURIComponent(value)}; max-age=${maxAgeSeconds}; path=/; SameSite=Lax`;
}

/**
 * Captures UTM parameters from the URL on first visit and persists them in a
 * cookie (90 days) so attribution survives tab-close and cross-tab booking.
 * (sessionStorage was lost when the user returned in a new tab — _fbc survived
 * as a cookie, utm did not. This fixes that asymmetry.)
 * First-touch attribution: does NOT overwrite existing values.
 * Mount in App root so it fires on every page (user may land on /gassi-service).
 */
export function useUtmCapture() {
  useEffect(() => {
    sitzungsEinstiegMerken();
    attributionSpeichern();
  }, []);
}

/**
 * Schreibt die Herkunft dieser Sitzung als Cookie, soweit die Einwilligung reicht (beim Laden und
 * nach einer Entscheidung im Banner). First-Touch-Regeln wie bisher: utm_params und first_touch
 * werden nie überschrieben, click_ids je Feld nur ergänzt, last_paid_click von jedem neuen
 * bezahlten Klick überschrieben. Wirft nie.
 */
export function attributionSpeichern(): void {
  if (typeof window === 'undefined') return;
  sitzungsEinstiegMerken();
  // Teilen v1 (T1): ?ref= aus geteilten Links auf jeder Seite merken (Speicher, Cookie nur mit Statistik).
  refMerken();
  const einstieg = sitzungsEinstieg;
  if (!einstieg) return;

  if (darfMessungSpeichern()) {
    // First-touch referrer + landing page — captured independently of UTM so
    // untagged traffic (Google-Suche, ChatGPT/Perplexity, Organic Social) is
    // still classifiable. Only external referrers count; own-domain referrers
    // (SPA reloads, internal nav) are noise, not a source.
    try {
      if (!readCookie(FIRST_TOUCH_KEY)) {
        const ft = { referrer: einstieg.referrer, landing_page: einstieg.landing_page };
        writeCookie(FIRST_TOUCH_KEY, JSON.stringify(ft), UTM_COOKIE_MAX_AGE);
      }
    } catch { /* tracking must never break the page */ }

    // First-touch: don't overwrite an already-captured value.
    try {
      if (!readCookie(UTM_STORAGE_KEY) && Object.keys(einstieg.utm).length > 0) {
        const json = JSON.stringify(einstieg.utm);
        writeCookie(UTM_STORAGE_KEY, json, UTM_COOKIE_MAX_AGE);
        // Best-effort mirror for same-session reads; cookie is the source of truth.
        try { sessionStorage.setItem(UTM_STORAGE_KEY, json); } catch { /* ignore */ }
      }
    } catch { /* tracking must never break the page */ }
  }

  if (darfMarketingSpeichern()) {
    // Klick-IDs unabhaengig von den utm_*-Parametern, s. Kommentar an CLICK_ID_STORAGE_KEY.
    // First-touch je Feld: ein bereits gespeicherter Wert wird nie ueberschrieben, eine noch
    // fehlende ID darf aber nachtragen werden (gclid und fbclid kommen aus verschiedenen
    // Plattformen und damit fast nie im selben Aufruf).
    try {
      const stored: ClickIds = JSON.parse(readCookie(CLICK_ID_STORAGE_KEY) || '{}');
      let changed = false;
      for (const key of CLICK_ID_KEYS) {
        const value = einstieg.klickIds[key];
        if (value && !stored[key]) {
          stored[key] = value;
          changed = true;
        }
      }
      if (changed) {
        const raw = JSON.stringify(stored);
        writeCookie(CLICK_ID_STORAGE_KEY, raw, UTM_COOKIE_MAX_AGE);
        try { sessionStorage.setItem(CLICK_ID_STORAGE_KEY, raw); } catch { /* ignore */ }
      }
    } catch { /* tracking must never break the page */ }

    // Letzter bezahlter Klick: überschreibt bewusst (nur wenn diese Sitzung über einen kam).
    try {
      if (einstieg.letzterKlick) writeCookie(LAST_PAID_KEY, JSON.stringify(einstieg.letzterKlick), UTM_COOKIE_MAX_AGE);
    } catch { /* tracking must never break the page */ }
  }
}

// Einstieg DIESER Sitzung im Speicher (16.10.2026). Grund: Eine HuTa-Anmeldung aus der
// Meta-Kampagne (Instagram-In-App-Browser, iOS) kam ohne UTM, Referrer und Einstiegsseite an,
// obwohl web_sessions alles hatte. Die beim Laden geschriebenen Cookies (utm_params, first_touch,
// last_paid_click) und der sessionStorage-Spiegel waren beim Absenden weg, Werte im JS-Speicher
// (fbclid-Stash, Sitzungs-ID) dagegen noch da. Der Speicher-Snapshot ist deshalb der Rückfall,
// wenn die Cookies fehlen. Er überschreibt nie einen gespeicherten First-Touch.
type SitzungsEinstieg = {
  utm: UtmParams;
  referrer: string | null;
  landing_page: string;
  /** Klick-IDs der Einstiegs-URL (nur Speicher; Cookie nur mit Marketing-Einwilligung). */
  klickIds: ClickIds;
  /** Bezahlter Klick dieser Sitzung (nur Speicher; Cookie nur mit Marketing-Einwilligung). */
  letzterKlick: LetzterBezahlterKlick | null;
};
let sitzungsEinstieg: SitzungsEinstieg | null = null;

function sitzungsEinstiegMerken(): void {
  if (sitzungsEinstieg || typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    const utm: UtmParams = {};
    for (const key of UTM_KEYS) {
      const value = params.get(key);
      if (value) utm[key] = value;
    }
    const klickIds: ClickIds = {};
    for (const key of CLICK_ID_KEYS) {
      const value = params.get(key);
      if (value) klickIds[key] = value;
    }
    let letzterKlick: LetzterBezahlterKlick | null = null;
    try {
      letzterKlick = bezahlterKlickAus(params, new Date().toISOString(), window.location.pathname);
    } catch { /* ignore */ }
    const ref = document.referrer || '';
    const isExternal = ref && !ref.includes(window.location.hostname);
    sitzungsEinstieg = {
      utm, referrer: isExternal ? ref : null, landing_page: window.location.pathname, klickIds, letzterKlick,
    };
  } catch { /* tracking must never break the page */ }
}

export type LeadAttribution = {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  referrer: string | null;
  landing_page: string | null;
};

/**
 * Attribution für ein Lead-Formular: First-Touch-Cookies (90 Tage), fehlt dort etwas, der
 * Einstieg dieser Sitzung aus dem Speicher. landing_page ist immer eine Einstiegsseite
 * (First-Touch oder Sitzung), nie die Seite, auf der gerade abgesendet wird.
 * UTM wird als Block übernommen (nie Quelle aus dem Cookie mit Kampagne aus der Sitzung mischen).
 */
export function getLeadAttribution(): LeadAttribution {
  sitzungsEinstiegMerken();
  const cookieUtm = getUtmParams();
  const utm: UtmParams = cookieUtm && Object.keys(cookieUtm).length > 0 ? cookieUtm : sitzungsEinstieg?.utm ?? {};
  const ft = getFirstTouch();
  return {
    utm_source: utm.utm_source ?? null,
    utm_medium: utm.utm_medium ?? null,
    utm_campaign: utm.utm_campaign ?? null,
    utm_content: utm.utm_content ?? null,
    utm_term: utm.utm_term ?? null,
    referrer: ft?.referrer ?? (ft ? null : sitzungsEinstieg?.referrer ?? null),
    landing_page: ft?.landing_page ?? sitzungsEinstieg?.landing_page ?? null,
  };
}

/** Read the first-touch referrer + landing page (null if never captured). */
export function getFirstTouch(): { referrer: string | null; landing_page: string | null } | null {
  try {
    const raw = darfMessungSpeichern() ? readCookie(FIRST_TOUCH_KEY) : undefined;
    if (raw) return JSON.parse(raw);
  } catch { /* fällt auf den Einstieg dieser Sitzung zurück */ }
  // Ohne Cookie (keine Einwilligung oder noch nicht geschrieben): Einstieg dieser Sitzung.
  sitzungsEinstiegMerken();
  return sitzungsEinstieg ? { referrer: sitzungsEinstieg.referrer, landing_page: sitzungsEinstieg.landing_page } : null;
}

/**
 * Read the first-touch ad click ids (gclid/fbclid).
 *
 * Additiv zu getUtmParams() — dessen Signatur bleibt unveraendert, weil sie von
 * AuthContext, NewsletterSignup und DaycareInterestCard mitbenutzt wird.
 *
 * Reihenfolge: Cookie → sessionStorage → aktuelle URL. Die URL ist nur der
 * Notnagel fuer den Fall, dass der Capture-Effect noch nicht gelaufen ist
 * (erster Render, Direktaufruf mit ?gclid=). Gibt immer ein Objekt zurueck.
 */
export function getClickIds(): ClickIds {
  const out: ClickIds = {};
  if (darfMarketingSpeichern()) {
    try {
      const raw = readCookie(CLICK_ID_STORAGE_KEY) ?? sessionStorage.getItem(CLICK_ID_STORAGE_KEY);
      if (raw) Object.assign(out, JSON.parse(raw));
    } catch { /* ignore */ }
  }
  sitzungsEinstiegMerken();
  for (const key of CLICK_ID_KEYS) {
    const value = sitzungsEinstieg?.klickIds[key];
    if (value && !out[key]) out[key] = value;
  }

  try {
    const params = new URLSearchParams(window.location.search);
    for (const key of CLICK_ID_KEYS) {
      const value = params.get(key);
      if (value && !out[key]) out[key] = value;
    }
  } catch { /* ignore */ }

  return out;
}

/** Read stored UTM params (cookie first, sessionStorage fallback; null if none). */
export function getUtmParams(): UtmParams | null {
  try {
    const raw = darfMessungSpeichern()
      ? readCookie(UTM_STORAGE_KEY) ?? sessionStorage.getItem(UTM_STORAGE_KEY)
      : null;
    if (raw) return JSON.parse(raw);
  } catch { /* fällt auf den Einstieg dieser Sitzung zurück */ }
  sitzungsEinstiegMerken();
  const utm = sitzungsEinstieg?.utm;
  return utm && Object.keys(utm).length > 0 ? utm : null;
}

/** Letzter bezahlter Klick (überschreibbar, 90 Tage) oder null. Ergänzt den First-Touch, ersetzt ihn nicht. */
export function getLastPaidClick(): LetzterBezahlterKlick | null {
  sitzungsEinstiegMerken();
  if (sitzungsEinstieg?.letzterKlick) return sitzungsEinstieg.letzterKlick;
  if (!darfMarketingSpeichern()) return null;
  try {
    return letztenKlickLesen(readCookie(LAST_PAID_KEY));
  } catch {
    return null;
  }
}
